import React, { Component } from 'react'
import { Button, Icon, Pagination, Table, Tag, Tooltip, message } from 'antd'
import { files } from '@services/api'
import { formatAmount, statusColor, statusText, operationType } from '../../utils'
import {
    CANCEL_SIDE_LABEL,
    OBJECT_URL_TTL_MS,
    PRIMARY_SIDE_LABEL,
    clearingText,
    documentActionText,
    pdfActionKey,
    pdfActionsForRow
} from '../utils'

// antd 3.23 has no column.ellipsis (it arrived in 3.25), so long text is clipped with the same
// Tooltip wrapper Tranzila/List uses. Without it a long email or product string stretches its
// column and breaks the width sum that scroll.x depends on.
const clip = (title, children = null, style = {}) => (
    <Tooltip title={title || ''}>
        <span
            style={Object.assign(
                {
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                },
                style
            )}
        >
            {children || title || '—'}
        </span>
    </Tooltip>
)

const DASH = '—'

// Three states, not two: no referenced document / referenced but unresolved / resolved.
// A referenced-but-unresolved document is REAL and must stay visible -- rendering it as an
// em-dash would read as "this transaction has no document", which is a different fact.
const documentCell = document => {
    if (!document) return <span style={{ color: '#bfbfbf' }}>{DASH}</span>

    if (!document.resolved) {
        return clip(
            `מסמך קיים (${document.reference_id}) — פרטיו טרם נמשכו`,
            <span style={{ color: '#8c8c8c', fontStyle: 'italic' }}>טרם נמשך</span>
        )
    }

    const action = documentActionText(document.action)
    return (
        <span style={{ display: 'block', lineHeight: '1.25rem' }}>
            <span dir="ltr">{document.number || DASH}</span>
            {action ? (
                <span style={{ display: 'block', fontSize: '0.75rem', color: '#8c8c8c' }}>{action}</span>
            ) : null}
        </span>
    )
}

export default class DocumentsList extends Component<any, any> {
    // In-flight guard lives outside state so a second click is rejected synchronously, before
    // React has re-rendered the disabled button.
    pdfRequests = {}
    // The PDF request can outlive the page (navigation away mid-download), so every setState
    // after an await is gated on this. React 16.6 has no cancellation primitive of its own.
    unmounted = false
    state = { pdfLoading: {} }

    componentWillUnmount() {
        this.unmounted = true
    }

    setPdfLoading = (key, loading) => {
        if (this.unmounted) return
        this.setState(prev => {
            const pdfLoading = Object.assign({}, prev.pdfLoading)
            if (loading) pdfLoading[key] = true
            else delete pdfLoading[key]
            return { pdfLoading }
        })
    }

    openPdf = (row, action) => {
        const key = pdfActionKey(row, action.key)
        if (this.pdfRequests[key]) return

        // Reserve the tab SYNCHRONOUSLY, inside the click handler, while the browser still
        // treats this as a user-activated gesture. Opening after the await loses that
        // activation and the popup blocker eats the tab.
        const reserved = window.open('', '_blank')
        if (!reserved) {
            message.error('לא ניתן לפתוח חלון חדש — יש לאפשר חלונות קופצים')
            return
        }

        this.pdfRequests[key] = true
        this.setPdfLoading(key, true)

        // The existing retrieval-key endpoint. The transaction-report PDF endpoint is NOT used
        // here: a retrieval key is already stored, so there is nothing to look up at Tranzila.
        files
            .getTranzilaInvoice({ retrieval_key: action.retrieval_key })
            .then(res => {
                const blob = new Blob([res.data], { type: 'application/pdf' })
                const url = window.URL.createObjectURL(blob)
                if (reserved.closed) {
                    // The operator closed the reserved tab while the bytes were in flight.
                    window.URL.revokeObjectURL(url)
                    return
                }
                // replace() so the blank placeholder does not become a back-button entry.
                reserved.location.replace(url)
                // Never revoked synchronously -- the tab has not loaded the bytes yet. The
                // timer releases the blob afterwards instead of leaking it for the session.
                window.setTimeout(() => window.URL.revokeObjectURL(url), OBJECT_URL_TTL_MS)
            })
            .catch(err => {
                if (!reserved.closed) reserved.close()
                const status = err && err.response && err.response.status
                // A failed fetch is still worth reporting even if the page has gone; antd's
                // message is global and unmount-safe, unlike setState.
                message.error(status === 404 ? 'המסמך לא נמצא' : 'פתיחת המסמך נכשלה')
            })
            .then(() => {
                delete this.pdfRequests[key]
                this.setPdfLoading(key, false)
            })
    }

    // A spinner while THIS row's detail is loading, the info-circle otherwise. The page owns the
    // request; this table only reports which row was asked for, by its cache id.
    transactionDetailsTrigger = row => {
        const { onOpenTransactionDetails, detailsLoadingId } = this.props
        if (!onOpenTransactionDetails) return null
        if (detailsLoadingId !== null && detailsLoadingId !== undefined && detailsLoadingId === row.id) {
            return <Icon type="loading" style={{ color: '#e77910', marginRight: 4 }} />
        }
        return (
            <Icon
                type="info-circle"
                style={{ color: '#e77910', cursor: 'pointer', marginRight: 4 }}
                onClick={() => onOpenTransactionDetails(row.id)}
            />
        )
    }

    // Columns live in one array so V2 can add, drop or reorder without touching anything else.
    getColumns = () => [
        // מסמכים first: the column an operator acts on leads the row. In RTL the first column
        // is the rightmost one. Moved as a whole -- renderer, actions and width unchanged.
        {
            title: 'מסמכים',
            key: 'pdf',
            width: 100,
            align: 'center',
            render: (_, row) => {
                const actions = pdfActionsForRow(row)
                if (!actions.length) return <span style={{ color: '#bfbfbf' }}>{DASH}</span>
                return (
                    <span>
                        {actions.map(action => {
                            const key = pdfActionKey(row, action.key)
                            const loading = !!this.state.pdfLoading[key]
                            return (
                                <Tooltip key={action.key} title={action.tooltip}>
                                    <Button
                                        shape="circle"
                                        size="small"
                                        icon={action.icon}
                                        loading={loading}
                                        disabled={loading}
                                        style={{ marginLeft: 4 }}
                                        onClick={() => this.openPdf(row, action)}
                                    />
                                </Tooltip>
                            )
                        })}
                    </span>
                )
            }
        },
        {
            title: 'תאריך',
            key: 'transaction_date',
            width: 120,
            align: 'right',
            render: (_, row) => (
                <span style={{ display: 'block', lineHeight: '1.25rem' }}>
                    <span>{row.transaction_date || DASH}</span>
                    <span style={{ display: 'block', fontSize: '0.75rem', color: '#8c8c8c' }}>
                        {row.transaction_time || ''}
                    </span>
                </span>
            )
        },
        {
            title: 'לקוח',
            key: 'client',
            width: 200,
            align: 'right',
            render: (_, row) => {
                // A cache row with no user_id stays visible; it simply shows Tranzila's own
                // client text instead of a linked identity. Nothing is inferred.
                if (row.user_id === null || row.user_id === undefined) {
                    return clip(row.user_defined_2 || '', null, { color: '#8c8c8c' })
                }
                const label = row.client_name || row.user_defined_2 || String(row.user_id)
                return clip(
                    `${label} (${row.user_id})`,
                    (
                        <a href={`/clients/${row.user_id}`} target="_blank" rel="noopener noreferrer">
                            {label} <span dir="ltr">({row.user_id})</span>
                        </a>
                    )
                )
            }
        },
        {
            title: 'אימייל',
            key: 'email',
            width: 200,
            align: 'right',
            render: (_, row) => clip(row.email, <span dir="ltr">{row.email || DASH}</span>)
        },
        {
            title: 'מס׳ עסקה',
            key: 'transaction_index',
            width: 110,
            align: 'right',
            // The Transactions page's own trigger: the orange info-circle beside the number opens
            // the shared details modal. The row itself stays unclickable -- it already carries a
            // client link and PDF buttons.
            render: (_, row) => (
                <span>
                    <span dir="ltr">{row.transaction_index}</span> {this.transactionDetailsTrigger(row)}
                </span>
            )
        },
        {
            title: 'מסוף',
            key: 'terminal_name',
            width: 130,
            align: 'right',
            render: (_, row) => <span dir="ltr">{row.terminal_name || DASH}</span>
        },
        {
            title: 'מק"ט / מוצר',
            key: 'sku',
            width: 200,
            align: 'right',
            render: (_, row) =>
                clip(
                    row.product_text || row.sku || '',
                    (
                        <span style={{ display: 'block', lineHeight: '1.25rem' }}>
                            <span dir="ltr">{row.sku || DASH}</span>
                            <span
                                style={{
                                    display: 'block',
                                    fontSize: '0.75rem',
                                    color: '#8c8c8c',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {row.product_text || ''}
                            </span>
                        </span>
                    )
                )
        },
        {
            title: 'סכום',
            key: 'amount',
            width: 100,
            align: 'right',
            // amount is agorot in the cache; formatAmount() is the existing agorot -> ₪ helper.
            render: (_, row) => <span dir="ltr">{formatAmount(row.amount) || DASH}</span>
        },
        {
            title: 'סולק',
            key: 'clearing',
            width: 110,
            align: 'right',
            render: (_, row) => clip(clearingText(row.mapped_clearing_processor) || DASH)
        },
        {
            title: 'סטטוס',
            key: 'status',
            width: 190,
            align: 'right',
            // Status vocabulary comes from the existing classifier/display helpers. This page
            // does not define statuses of its own.
            render: (_, row) => (
                <Tag
                    color={statusColor(row.group)}
                    style={{ display: 'block', width: '100%', maxWidth: '100%', margin: 0, overflow: 'hidden' }}
                >
                    {clip(statusText(row), null, { textAlign: 'center' })}
                </Tag>
            )
        },
        {
            title: 'סוג',
            key: 'kind',
            width: 120,
            align: 'right',
            render: (_, row) => clip(operationType(row))
        },
        {
            title: PRIMARY_SIDE_LABEL,
            key: 'primary_document',
            width: 120,
            align: 'right',
            render: (_, row) => documentCell(row.primary_document)
        },
        {
            title: CANCEL_SIDE_LABEL,
            key: 'cancellation_document',
            width: 120,
            align: 'right',
            render: (_, row) => documentCell(row.cancellation_document)
        }
    ]

    render() {
        const { rows, loading, page, perPage, total, onPageChange } = this.props
        const columns = this.getColumns()
        // Without scroll.x the table is cut off by the viewport instead of scrolling.
        const totalWidth = columns.reduce((sum, column) => sum + column.width, 0)

        return (
            <div style={{ minWidth: 0 }}>
                <div className="box">
                    <Table
                        className="center-table"
                        style={{ width: '100%' }}
                        scroll={{ x: totalWidth }}
                        rowKey="id"
                        size="middle"
                        bordered
                        columns={columns}
                        dataSource={rows}
                        loading={loading}
                        rowClassName={(_, index) => (index % 2 ? 'row-odd' : 'row-even')}
                        locale={{ emptyText: 'אין מסמכים להצגה' }}
                        pagination={false}
                    />
                </div>
                <div style={{ textAlign: 'left', padding: '12px 16px' }}>
                    <Pagination
                        current={page}
                        pageSize={perPage}
                        total={total}
                        showSizeChanger={false}
                        showTotal={totalRows => `סה״כ ${totalRows} רשומות`}
                        onChange={onPageChange}
                    />
                </div>
            </div>
        )
    }
}
